import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, IconButton, Stack, Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteIcon,
  OpenInFull as FullScreenIcon,
  Close as CloseIcon,
  UploadFileOutlined as UploadIcon,
} from '@mui/icons-material';
import FilesShell, { useFileUpload } from '../../components/files/FilesShell';
import PageHeader from '../../components/ui/PageHeader';
import { LoadingState } from '../../components/ui/States';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import DocumentService, { DocumentType, TopOpsDocument } from '../../api/services/document.service';
import {
  DocumentPill, ExpiryLine, GeneratedName, LockChip, formatBytes, categoryLabel,
} from '../../components/files/vaultUI';
import FilePreview, { FilePreviewError } from '../../components/files/FilePreview';
import { downloadBlob } from '../../utils/download';

// One document, read at the size a document should be read at.
//
// It used to be a 640px-tall frame inside a card inside the shared 1280px
// column, next to a details card — so a full page of paper rendered at
// about a third of the window and you were expected to squint at it. It is
// a reading screen now: the viewer takes the whole height of the window,
// the facts move to a narrow rail beside it, and there is a full-screen
// mode that hides even that.
//
// There is no signed URL for a document — the vault streams bytes through
// the API, access is re-checked per read, and sensitive reads are audited.
// So the preview cannot point an <iframe> at a URL: the blob is fetched
// with the caller's token and shown from an object URL, which is revoked on
// unmount. A URL copied out of this page is useless to anybody else, and
// stops working for this person the moment their access changes.

// Has to be its own component: it reads the shell's upload context, which
// only exists inside <FilesShell>.
const FileRenewalButton: React.FC<{ document: TopOpsDocument }> = ({ document }) => {
  const openUpload = useFileUpload();
  return (
    <Button
      variant="contained"
      startIcon={<UploadIcon />}
      onClick={() => openUpload({
        typeKey: document.documentTypeKey,
        // Company paper has no owner to lock — and locking it to a label
        // like "Company-wide" would write that into the filename.
        owner: document.ownerType === 'company' || !document.ownerId
          ? undefined
          : { ownerType: document.ownerType, ownerId: document.ownerId, label: document.ownerLabel || '' },
      })}
    >
      File renewal
    </Button>
  );
};

const DocumentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();

  const [document, setDocument] = useState<TopOpsDocument | null>(null);
  const [type, setType] = useState<DocumentType | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  // The bytes themselves, for the previews read from them (a CSV's table).
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      const detail = await DocumentService.getDetail(documentId);
      setDocument(detail.document);
      setType(detail.type);
      setError(null);
    } catch (err) {
      // A document this caller may not see returns 404, so "not found"
      // genuinely means "nothing to show you".
      setError(err instanceof Error ? err.message : 'Could not open this document');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { load(); }, [load]);

  usePageBreadcrumb(type ? [{ label: type.label }] : []);

  // Fetch the bytes separately from the metadata: a preview that fails
  // (an odd content type, a slow link) must not take the whole page with it.
  useEffect(() => {
    if (!documentId || !document) return;
    let revoked = false;
    let url: string | null = null;

    DocumentService.getFileBlob(documentId)
      .then((blob) => {
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setFileBlob(blob);
        setObjectUrl(url);
      })
      .catch((err) => setPreviewError(err instanceof Error ? err.message : 'Could not load the preview'));

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
      setObjectUrl(null);
      setFileBlob(null);
    };
  }, [documentId, document]);

  const handleDownload = async () => {
    if (!documentId || !document) return;
    try {
      const blob = await DocumentService.getFileBlob(documentId);
      downloadBlob(blob, document.fileName);
      // Saving a copy is the document leaving the app, and the vault wants
      // that on the record. Never blocking.
      DocumentService.recordShared(documentId, 'download');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download this file');
    }
  };

  const handleDelete = async () => {
    if (!documentId) return;
    setDeleting(true);
    try {
      await DocumentService.remove(documentId);
      navigate('/files/recently-deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete this document');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return <FilesShell><LoadingState label="Opening this document…" /></FilesShell>;
  }

  if (error && !document) {
    return (
      <FilesShell>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2, ml: -1 }}>Back</Button>
        <Alert severity="error">{error}</Alert>
      </FilesShell>
    );
  }

  // The viewer itself, so the inline frame and the full-screen dialog are
  // the same component rather than two that drift. What it draws for each
  // kind of file is FilePreview's business.
  //
  // `fit` differs between the two on purpose. Inline the frame is a tall
  // slot beside the details rail, so fitting the page WIDTH uses it best
  // and you scroll to read. Full screen the frame is the whole landscape
  // window, where fitting the width would blow a portrait page up past
  // 200% and make you scroll a certificate you wanted to see whole — so
  // there it fits the entire page.
  const renderViewer = (fit: 'FitH' | 'Fit') => (
    <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.100', display: 'flex', minHeight: 0 }}>
      {previewError ? (
        <FilePreviewError message={previewError} />
      ) : !objectUrl || !fileBlob ? (
        <Box sx={{ m: 'auto' }}><CircularProgress /></Box>
      ) : (
        <FilePreview
          blob={fileBlob}
          url={objectUrl}
          contentType={document?.file?.contentType}
          name={document?.file?.path}
          title={document?.fileName}
          fit={fit}
          onDownload={handleDownload}
        />
      )}
    </Box>
  );

  // The superseded copy is history; only the live one can be renewed.
  const renewable = !!document && document.status !== 'archived' && !document.supersededById;

  return (
    <FilesShell
      reader
      currentCategory={document?.category}
      // A filed renewal supersedes this document, so go and look at the new
      // one rather than staying on the copy that just became history.
      onUploaded={(newId) => navigate(`/files/document/${newId}`)}
      header={
        <PageHeader
          title={
            <Stack direction="row" gap={1.25} alignItems="center" flexWrap="wrap">
              <span>{type?.label || 'Document'}</span>
              {document && <DocumentPill status={document.status} />}
              {type?.sensitivity === 'restricted' && <LockChip />}
              {type?.sensitivity === 'confidential' && <LockChip label="Confidential" />}
            </Stack>
          }
          subtitle={document ? <GeneratedName name={document.fileName} /> : undefined}
          actions={
            <>
              <Button variant="outlined" startIcon={<FullScreenIcon />} onClick={() => setFullScreen(true)}>
                Full screen
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>Download</Button>
              {renewable && document && <FileRenewalButton document={document} />}
              <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>Delete</Button>
            </>
          }
        />
      }
      inspector={
        <Box sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Details</Typography>
          <Detail label="Filed against" value={document?.ownerLabel || 'Company-wide'} />
          <Detail label="Category" value={categoryLabel(document?.category || '')} />
          <Detail label="Requirement" value={type?.requirement} />
          <Detail label="Issued" value={document?.issuedAt ? new Date(document.issuedAt).toLocaleDateString() : '—'} />
          <Box sx={{ py: 0.75 }}>
            <Typography variant="caption" color="text.secondary" display="block">Expires</Typography>
            {document?.expiresAt
              ? <ExpiryLine expiresAt={document.expiresAt} />
              : <Typography variant="body2">Does not expire</Typography>}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Detail label="Size" value={formatBytes(document?.file?.sizeBytes)} />
          <Detail label="Uploaded" value={document?.uploadedAt ? new Date(document.uploadedAt).toLocaleString() : '—'} />
          <Detail
            label="Kept until"
            value={document?.retainUntil ? new Date(document.retainUntil).toLocaleDateString() : '—'}
          />
          {document?.legalHold && (
            <Alert severity="info" sx={{ mt: 1 }}>
              On legal hold — it cannot be purged while a case depends on it.
            </Alert>
          )}
          {document?.notes && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block">Notes</Typography>
              <Typography variant="body2">{document.notes}</Typography>
            </>
          )}
          {type?.regulatoryCite && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              {type.regulatoryCite}
            </Typography>
          )}
        </Box>
      }
    >
      <Card sx={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex' }}>
        {renderViewer('FitH')}
      </Card>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Nothing but the paper. The object URL is the same one the inline
          frame uses, so opening this costs no second fetch. */}
      <Dialog fullScreen open={fullScreen} onClose={() => setFullScreen(false)}>
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>{type?.label || 'Document'}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {document?.fileName}
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>Download</Button>
          <IconButton onClick={() => setFullScreen(false)} aria-label="Close full screen">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0 }}>{renderViewer('Fit')}</Box>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete this document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It moves to Recently Deleted and can be restored for 30 days. After that it is gone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </FilesShell>
  );
};

const Detail: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box sx={{ py: 0.75 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

export default DocumentDetailPage;
